import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, X } from 'lucide-react';
import { ServiceSubscription, CreateServiceSubscriptionRequest, UpdateServiceSubscriptionRequest, ServiceType, ServiceSolution, ServiceStatus, GAMType, SERVICE_TO_SOLUTIONS, INITIAL_FORM_DATA, ServiceSubscriptionFormData } from '../../types/service';
import { Domain } from '../../types/network';
import { getServiceTypes } from '../../services/services';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { ServiceFormFields, getIdentifierFieldForSolution } from './ServiceFormFields';

export interface QuickAddDefaults {
  service: ServiceType;
  solution?: ServiceSolution;
  websiteNameMapping?: string;
}

interface ServiceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: Domain;
  service?: ServiceSubscription | null;
  onSave: (service: ServiceSubscription) => void;
  quickAddDefaults?: QuickAddDefaults | null;
}

type FormData = ServiceSubscriptionFormData;
const initialFormData: FormData = { ...INITIAL_FORM_DATA };

export const ServiceEditDialog: React.FC<ServiceEditDialogProps> = ({
  open,
  onOpenChange,
  domain,
  service,
  onSave,
  quickAddDefaults = null
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  interface ServiceTypesMeta { serviceTypes: ServiceType[]; solutions: ServiceSolution[]; statuses: ServiceStatus[]; gamTypes?: GAMType[] }
  const [serviceTypesData, setServiceTypesData] = useState<ServiceTypesMeta | null>(null);

  const isEditing = !!service;
  const isQuickAdd = !!quickAddDefaults && !isEditing;

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const data = await getServiceTypes();
        // Inject gamTypes if backend (older version) does not provide them
        const enriched: ServiceTypesMeta = {
          serviceTypes: (data as any).serviceTypes || Object.values(ServiceType),
          solutions: (data as any).solutions || Object.values(ServiceSolution),
            statuses: (data as any).statuses || Object.values(ServiceStatus),
          gamTypes: (data as any).gamTypes || Object.values(GAMType)
        };
        setServiceTypesData(enriched);
      } catch (e) {
        console.error('getServiceTypes failed', e);
      }
    };
    if (open) fetchTypes();
  }, [open]);

  useEffect(() => {
    if (open) {
      if (service) {
        console.log('ServiceEditDialog: Loading service for edit', service); // Debug log
        setFormData({
          service: service.service,
          solution: service.solution,
          status: service.status,
          commissionPublisher: service.commissionPublisher ?? null,
          commissionDblock: service.commissionDblock ?? null,
          commissionPercentage: service.commissionPercentage ?? null,
          gamType: service.gamType ?? null,
          gamAccountId: service.gamAccountId ?? null,
          scriptVariant: service.scriptVariant ?? null,
          integrationType: service.integrationType ?? null,
          protocol: service.protocol ?? null,
          outbrainSection: (service as any).outbrainSection ?? service.config?.outbrainSection ?? null,
          websiteName: (service as any).websiteName ?? service.config?.websiteName ?? null,
          siteKey: (service as any).siteKey ?? service.config?.siteKey ?? null,
          startDate: service.startDate.split('T')[0],
          endDate: service.endDate ? service.endDate.split('T')[0] : '',
          details: service.details || ''
        });
        console.log('ServiceEditDialog: Formatted endDate', service.endDate ? service.endDate.split('T')[0] : 'No endDate'); // Debug log
      } else if (quickAddDefaults) {
        const defaultSolution = quickAddDefaults.solution || SERVICE_TO_SOLUTIONS[quickAddDefaults.service][0];
        setFormData({ ...initialFormData, service: quickAddDefaults.service, solution: defaultSolution });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [open, service, quickAddDefaults]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.service) newErrors.service = t('serviceEdit.errors.serviceRequired');
    if (!formData.solution) newErrors.solution = t('serviceEdit.errors.solutionRequired', 'Solution required');
    if (!formData.startDate) newErrors.startDate = t('serviceEdit.errors.startDateRequired');
    if (formData.endDate && formData.endDate <= formData.startDate) newErrors.endDate = t('serviceEdit.errors.endDateAfterStart');
    if (formData.commissionPercentage != null) {
      const c = formData.commissionPercentage;
      if (c! < 0 || c! > 100) newErrors.commissionPercentage = t('serviceEdit.errors.invalidCommission');
    }
    if (formData.details) { try { JSON.parse(formData.details); } catch { newErrors.details = t('serviceEdit.errors.invalidJson'); } }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const createPayload: CreateServiceSubscriptionRequest = {
        domainId: domain.id,
        service: formData.service as ServiceType,
        solution: formData.solution as ServiceSolution,
        status: formData.status as ServiceStatus,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        details: formData.details || undefined,
        commissionPublisher: formData.commissionPublisher ?? undefined,
        commissionDblock: formData.commissionDblock ?? undefined,
        commissionPercentage: formData.commissionPercentage ?? undefined,
        gamType: formData.gamType ?? undefined,
        gamAccountId: formData.gamAccountId ?? undefined,
        scriptVariant: formData.scriptVariant ?? undefined,
        integrationType: formData.integrationType ?? undefined,
        protocol: formData.protocol ?? undefined,
        outbrainSection: formData.outbrainSection ?? undefined,
        websiteName: formData.websiteName ?? undefined,
        siteKey: formData.siteKey ?? undefined
      };

      let result: ServiceSubscription;
      if (isEditing && service) {
        const updatePayload: UpdateServiceSubscriptionRequest = {
          status: formData.status,
          endDate: formData.endDate || null,
          details: formData.details || undefined,
          commissionPublisher: formData.commissionPublisher ?? undefined,
          commissionDblock: formData.commissionDblock ?? undefined,
          commissionPercentage: formData.commissionPercentage ?? undefined,
          gamType: formData.gamType ?? undefined,
          gamAccountId: formData.gamAccountId ?? undefined,
          scriptVariant: formData.scriptVariant ?? undefined,
          integrationType: formData.integrationType ?? undefined,
          protocol: formData.protocol ?? undefined,
          outbrainSection: formData.outbrainSection ?? undefined,
          websiteName: formData.websiteName ?? undefined,
          siteKey: formData.siteKey ?? undefined
        };
        result = await import('../../services/services').then(m => m.updateService(service.id, updatePayload));
      } else {
        result = await import('../../services/services').then(m => m.createService(createPayload));
      }
      onSave(result);
      onOpenChange(false);
    } catch (e: any) {
      console.error('Error saving service', e);
      setErrors({ submit: e?.response?.data?.error || t('serviceEdit.errors.saveFailed') });
    } finally { setIsLoading(false); }
  };

  if (!serviceTypesData) return null;

  const identifierField = getIdentifierFieldForSolution(formData.solution as ServiceSolution);
  const isEditingMapping = !!service && !!identifierField;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('serviceEdit.editService') : (isQuickAdd ? t('serviceEdit.quickAddTitle', 'Ajout rapide service') : t('serviceEdit.addService'))}
          </DialogTitle>
          <DialogDescription>
            {isQuickAdd ? t('serviceEdit.quickAddDescription', 'Création rapide pour {{domain}}', { domain: domain.name }) : t('serviceEdit.description', { domain: domain.name })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {errors.submit && (
            <Alert variant="destructive"><AlertDescription>{errors.submit}</AlertDescription></Alert>
          )}
          <ServiceFormFields
            data={formData}
            onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
            lockService={isEditing}
            lockSolution={isEditing}
            mode={isEditing ? 'edit' : (quickAddDefaults ? 'quickadd' : 'create')}
            identifierVisibility={isEditing ? 'readonly' : 'editable'}
          />
          {isEditingMapping && identifierField && (
            <p className="text-xs text-muted-foreground -mt-2">{t('serviceEdit.mappingImmutable','La clé de mapping ne doit idéalement pas être modifiée après création.')}</p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('serviceEdit.startDate')} *</Label>
              <Input type="date" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.target.value)} />
              {errors.startDate && <p className="text-sm text-destructive">{errors.startDate}</p>}
            </div>
            <div className="space-y-2">
              <Label>{t('serviceEdit.endDate')}</Label>
              <Input type="date" value={formData.endDate} onChange={(e) => handleInputChange('endDate', e.target.value)} />
              {errors.endDate && <p className="text-sm text-destructive">{errors.endDate}</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('serviceEdit.details')}</Label>
            <Textarea value={formData.details} onChange={(e) => handleInputChange('details', e.target.value)} placeholder='{"additionalConfig": "value"}' rows={3} />
            {errors.details && <p className="text-sm text-destructive">{errors.details}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            <X className="mr-2 h-4 w-4" /> {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            <Save className="mr-2 h-4 w-4" /> {isLoading ? t('common.saving') : (isEditing ? t('common.save') : t('common.create'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};